import React, { useState } from 'react';

const DEFAULT_HEADERS = [
  'Name',
  'ProductGroup',
  'SKU',
  'Barcode',
  'MeasurementUnit',
  'Cost',
  'Markup',
  'Price',
  'IsTaxInclusivePrice',
  'IsService',
  'IsEnabled',
  'Description',
  'Quantity'
];

function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = lines[0].split(',').map((h) => h.trim().toLowerCase().replace(/\s+/g, ''));
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map((v) => v.trim());
    const row = {};
    headers.forEach((h, j) => {
      row[h] = values[j] !== undefined ? values[j] : '';
    });
    rows.push(row);
  }
  return { headers, rows };
}

export default function ProductCsvImport({ onImport, onImportFromBackend, authConfig, onUnauthorized }) {
  const [file, setFile] = useState(null);
  const [duplicateMode, setDuplicateMode] = useState('update');
  const [loading, setLoading] = useState(false);
  const [importBackendLoading, setImportBackendLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setImportProgress(null);
    setMessage('');
    setError('');
  };

  const handleImport = async () => {
    if (!file || !onImport) return;
    setLoading(true);
    setImportProgress(null);
    setMessage('');
    setError('');
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const text = ev.target?.result || '';
        const { rows } = parseCsv(text);
        if (rows.length === 0) {
          setError('No data rows in CSV.');
          setLoading(false);
          return;
        }
        setImportProgress({
          total: rows.length,
          processed: 0,
          imported: 0,
          failed: 0,
          remaining: rows.length,
          phase: 'importing'
        });
        const result = await onImport(rows, authConfig, onUnauthorized, (p) => {
          setImportProgress(normalizeProgress({ ...p, total: rows.length, phase: 'importing' }));
        }, text, duplicateMode);
        setImportProgress(normalizeProgress({
          total: rows.length,
          processed: rows.length,
          imported: result.imported ?? 0,
          failed: result.errors?.length || Math.max(0, rows.length - (result.imported ?? 0)),
          remaining: 0,
          phase: 'done',
          created: result.created,
          updated: result.updated,
          merged: result.merged,
          skipped: result.skipped
        }));
        setMessage(result.message || `Imported ${result.imported ?? 0} products.`);
        if (result.errors?.length) setError(formatImportErrors(result.errors));
        setFile(null);
      } catch (err) {
        setError(err?.message || 'Import failed.');
      } finally {
        setLoading(false);
      }
    };
    reader.readAsText(file);
  };

  const handleImportFromBackend = async () => {
    if (!onImportFromBackend) return;
    setImportBackendLoading(true);
    setImportProgress(null);
    setMessage('');
    setError('');
    try {
      const result = await onImportFromBackend(authConfig, onUnauthorized, (p) => {
        setImportProgress(normalizeProgress({ ...p, phase: 'importing' }));
      }, duplicateMode);
      if (result.total > 0) {
        setImportProgress(normalizeProgress({
          total: result.total,
          processed: result.total,
          imported: result.imported ?? 0,
          failed: Math.max(0, result.total - (result.imported ?? 0)),
          remaining: 0,
          phase: 'done',
          created: result.created,
          updated: result.updated,
          merged: result.merged,
          skipped: result.skipped
        }));
      }
      setMessage(result.message || `Imported ${result.imported ?? 0} products.`);
      if (result.errors?.length) setError(formatImportErrors(result.errors));
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Import failed.');
    } finally {
      setImportBackendLoading(false);
    }
  };

  const busy = loading || importBackendLoading;

  return (
    <div className="border rounded p-3 bg-gray-50 space-y-2">
      <h4 className="font-semibold text-sm">CSV import</h4>
      <p className="text-xs text-gray-600">
        CSV with header: {DEFAULT_HEADERS.join(', ')}. Price and name required.
      </p>
      <div className="rounded border bg-white p-3 space-y-2">
        <p className="text-sm font-medium text-gray-800">What happens if duplicates are found?</p>
        <div className="grid gap-2 text-sm sm:grid-cols-3">
          <label className="flex items-start gap-2 rounded border p-2">
            <input
              type="radio"
              name="duplicateMode"
              value="update"
              checked={duplicateMode === 'update'}
              onChange={(e) => setDuplicateMode(e.target.value)}
              disabled={busy}
            />
            <span>
              <span className="block font-medium">Update duplicates</span>
              <span className="block text-xs text-gray-500">Default. Existing item details and stock are replaced.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 rounded border p-2">
            <input
              type="radio"
              name="duplicateMode"
              value="skip"
              checked={duplicateMode === 'skip'}
              onChange={(e) => setDuplicateMode(e.target.value)}
              disabled={busy}
            />
            <span>
              <span className="block font-medium">Skip duplicates</span>
              <span className="block text-xs text-gray-500">Existing items are left unchanged.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 rounded border p-2">
            <input
              type="radio"
              name="duplicateMode"
              value="merge"
              checked={duplicateMode === 'merge'}
              onChange={(e) => setDuplicateMode(e.target.value)}
              disabled={busy}
            />
            <span>
              <span className="block font-medium">Merge quantities</span>
              <span className="block text-xs text-gray-500">Existing stock quantity is increased by CSV quantity.</span>
            </span>
          </label>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input type="file" accept=".csv" onChange={handleFileChange} className="text-sm" />
        <button
          type="button"
          onClick={handleImport}
          disabled={!file || loading}
          className="bg-indigo-600 text-white px-3 py-1.5 rounded text-sm disabled:opacity-60"
        >
          {loading ? 'Importing...' : 'Import file'}
        </button>
        {onImportFromBackend && (
          <button
          type="button"
          onClick={handleImportFromBackend}
          disabled={importBackendLoading}
            className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm disabled:opacity-60 flex items-center gap-2"
          >
            {importBackendLoading && (
              <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />
            )}
            Import from products.csv
          </button>
        )}
      </div>
      {importProgress && importProgress.total > 0 && (
        <ImportProgressDetails progress={importProgress} busy={busy} />
      )}
      {message && <p className="text-sm text-green-700">{message}</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}

function normalizeProgress(progress) {
  const total = Math.max(0, Number(progress.total) || 0);
  const processed = Math.min(total, Math.max(0, Number(progress.processed ?? progress.imported) || 0));
  const imported = Math.max(0, Number(progress.imported) || 0);
  const failed = Math.max(0, Number(progress.failed) || Math.max(0, processed - imported));
  return {
    ...progress,
    total,
    processed,
    imported,
    failed,
    created: Math.max(0, Number(progress.created) || 0),
    updated: Math.max(0, Number(progress.updated) || 0),
    merged: Math.max(0, Number(progress.merged) || 0),
    skipped: Math.max(0, Number(progress.skipped) || 0),
    remaining: Math.max(0, Number(progress.remaining ?? (total - processed)) || 0)
  };
}

function formatImportErrors(errors) {
  const shown = errors.slice(0, 5).join('; ');
  return errors.length > 5 ? `${shown}; ... and ${errors.length - 5} more` : shown;
}

function ImportProgressDetails({ progress, busy }) {
  const percent = progress.total > 0 ? Math.min(100, Math.round((progress.processed / progress.total) * 100)) : 0;
  const isDone = progress.phase === 'done' || (!busy && progress.processed >= progress.total);

  return (
    <div className="rounded border border-emerald-200 bg-white p-3 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-gray-800">
          {isDone ? 'Import complete' : 'Importing products...'}
        </p>
        <p className="text-xs font-semibold text-emerald-700">{percent}%</p>
      </div>
      <div className="h-2 bg-gray-200 rounded overflow-hidden">
        <div
          className="h-full bg-emerald-500 transition-all duration-300"
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
        <ProgressStat label="Total rows" value={progress.total} />
        <ProgressStat label="Uploaded" value={progress.imported} />
        <ProgressStat label="Remaining" value={progress.remaining} />
        <ProgressStat label="Failed/skipped" value={progress.failed} />
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
        <ProgressStat label="Created" value={progress.created} />
        <ProgressStat label="Updated" value={progress.updated} />
        <ProgressStat label="Merged" value={progress.merged} />
        <ProgressStat label="Duplicates skipped" value={progress.skipped} />
      </div>
      <p className="text-xs text-gray-600">
        Processed {progress.processed} of {progress.total} rows.
      </p>
      {progress.current ? (
        <p className="truncate text-xs text-gray-500" title={progress.current}>
          Current: {progress.current}
        </p>
      ) : null}
    </div>
  );
}

function ProgressStat({ label, value }) {
  return (
    <div className="rounded bg-gray-50 p-2">
      <p className="text-[10px] uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-base font-semibold text-gray-900">{value}</p>
    </div>
  );
}
